
import throttle from 'lodash/throttle'
import EoIcon, { EoIconOptions } from '../EoIcon/EoIcon'
import EoImg from '../EoImg/EoImg'

export default {
  name: 'EoCard',
  components: {
    EoIcon,
    EoImg,
  },
  props: {
    /**
     * Alt for screenreaders
     */
    alt: {
      type: String,
      default: null,
    },
    /**
     * Hyperlink to url, page or anchor tag
     */
    to: {
      type: String,
      default: null,
    },
    /**
     * Prefetches the page behind the link
     */
    prefetch: {
      type: Boolean,
      default: true,
    },
    /**
     * Target for the link: (_self) (_blank)
     */
    target: {
      type: String,
      default: '_self',
    },
    /**
     * Image source OR source set
     */
    image: {
      type: String,
      default: null,
    },
    /**
     * See <eo-img> prop 'size'
     */
    imageSize: {
      type: [String, Number],
      default: null,
    },
    /**
     * See <eo-img> prop 'width'
     */
    imageWidth: {
      type: [String, Number],
      default: null,
    },
    /**
     * See <eo-img> prop 'focus'
     */
    imageFocus: {
      type: [String, Object],
      default: null,
    },
    /**
     * URL of a video OR multiple URls with different file extensions
     */
    video: {
      type: [String, Array],
      default: null,
    },
    /**
     * Loading attribute of image or video: (eager) (lazy)
     */
    loading: {
      type: String,
      validator: prop => ['eager', 'lazy', '', null].includes(prop),
      default: 'lazy',
    },
    /**
     * Title
     */
    title: {
      type: String,
      default: null,
    },
    /**
     * Subtitle
     */
    subtitle: {
      type: String,
      default: null,
    },
    /**
     * Icon in front of the subtitle
     */
    subtitleIcon: {
      type: String,
      validator: prop => EoIconOptions().includes(prop),
      default: null,
    },
    /**
     * Place subtitle below title
     */
    subtitleBelow: {
      type: Boolean,
      default: false,
    },
    /**
     * Multiline description or excerpt
     */
    description: {
      type: String,
      default: null,
    },
    /**
     * Show content type in upperleft corner: (video) (video-npo)
     */
    contentType: {
      validator: prop => Object.values(['video', 'video-npo']).includes(prop) || prop === undefined || prop === null,
      default: null,
    },
    /**
     * Style: small card with media on the side
     */
    small: {
      type: Boolean,
      default: false,
    },
    /**
     * Take up all the space in the parent container
     */
    fluid: {
      type: Boolean,
      default: false,
    },
    /**
     * Minimum height (percentage of width OR pixels)
     */
    minHeight: {
      type: String,
      default: null,
      validator: (value) => {
        return value.includes('px') || value.includes('%')
      },
    },
    /**
     * Minimum width (1-12 OR pixels)
     */
    minWidth: {
      type: [String, Number],
      default: null,
      validator: (value) => {
        if (Number(value)) {
          return 0 <= Number(value) && Number(value) <= 12
        }
        return true
      },
    },
    /**
     * Maximum width (1-12 OR pixels)
     */
    maxWidth: {
      type: [String, Number],
      default: null,
      validator: (value) => {
        if (Number(value)) {
          return 0 <= Number(value) && Number(value) <= 12
        }
        return true
      },
    },
    /**
     * Fixed width (1-12 OR pixels)
     */
    width: {
      type: [String, Number],
      default: null,
      validator: (value) => {
        if (Number(value)) {
          return 0 <= Number(value) && Number(value) <= 12
        }
        return true
      },
    },
    /**
     * Image: make it fill the entire background
     */
    imageFull: {
      type: Boolean,
      default: false,
    },
    /**
     * Image: try to keep a square ratio
     */
    imageSquare: {
      type: Boolean,
      default: false,
    },
    /**
     * Text: center align all content of card
     */
    textCentered: {
      type: Boolean,
      default: false,
    },
    /**
     * Text: place content before figure
     */
    textFirst: {
      type: Boolean,
      default: false,
    },
    /**
     * Text: color of the text in the card
     */
    textColor: {
      type: String,
      default: null,
    },
    /**
     * Title: font size: (sm) (md) (lg)
     */
    titleSize: {
      type: String,
      validator: prop => Object.values(['sm', 'md', 'lg']).includes(prop),
      default: 'md',
    },
    /**
     * Title: number of lines to truncate (must be greater or equal to 1)
     */
    titleLines: {
      type: [String, Number],
      default: null,
      validator: (value) => {
        if (Number(value)) {
          return Number(value) >= 1
        }
        return true
      },
    },
    /**
     * Background color of the card
     */
    color: {
      type: String,
      default: null,
    },
    /**
     * Let the card appear when it enters the viewport
     */
    appear: {
      type: Boolean,
      default: false,
    },
    /**
     * Hide or show the card (but preserve its needed space)
     */
    visible: {
      type: Boolean,
      default: true,
    },
    /**
     * Show card in a inactive or 'blurred' state
     */
    blurred: {
      type: Boolean,
      default: false,
    },
    /**
     * Disable user interaction with the card
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Show that the content has been read
     */
    read: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      throttle,
      intersectionStop: null,
      isLoading: true,
      isInViewport: true,
      isVisible: true,
      isFixedRatio: false,
      hasTouchMoved: false,
      styles: {
        minHeight: null,
        maxWidth: null,
        minWidth: null,
        width: null,
      },
      stylesRatio: {
        paddingBottom: null,
      },
    }
  },
  computed: {
    classes () {
      return {
        'eo-card': true,
        'is-clickable': !!this.to || this.hasClickListener,
        'is-small': this.small,
        'is-fluid': this.fluid,
        'is-image-full': this.imageFull,
        'is-image-square': this.imageSquare,
        'is-text-centered': this.textCentered,
        'is-text-first': this.textFirst,
        'is-subtitle-below': this.subtitleBelow,
        'is-blurred': this.blurred || this.disabled,
        'is-disabled': this.disabled,
        'is-fixed-ratio': this.isFixedRatio,
        'is-hidden': !this.isVisible,
        'is-loading': this.isLoading,
        'is-read': this.read,
        'has-float': this.hasFloatSlot,
        'has-no-content': !this.$slots.above && !this.subtitle && !this.title && !this.description && !this.$slots.default,
      }
    },
    classesTile () {
      return {
        'eo-card__tile': true,
        ['color--' + this.color]: !!this.color,
        ['text--' + this.textColor]: !!this.textColor,
      }
    },
    dynamicLinkTag () {
      if (this.to) {
        if (
          this.to.startsWith('https://') ||
          this.to.startsWith('http://') ||
          this.to.startsWith('tel:') ||
          this.to.startsWith('mailto:') ||
          this.to.startsWith('whatsapp:') ||
          this.to.startsWith('#') ||
          this.target.toLowerCase() === '_parent' ||
          this.target.toLowerCase() === '_top'
        ) {
          return 'a'
        }
        if (this.$root.$options.components.NuxtLink) {
          return 'nuxt-link'
        } else if (this.$root.$options.components.RouterLink) {
          return 'router-link'
        } else {
          return 'a'
        }
      }
      return 'div'
    },
    href () {
      if (this.dynamicLinkTag === 'a' || this.dynamicLinkTag === 'nuxt-link' || this.dynamicLinkTag === 'router-link') {
        return this.to || null
      } else {
        return null
      }
    },
    videoSources () {
      return Array.isArray(this.video) ? this.video : [this.video]
    },
    hasClickListener () {
      return this.$listeners && this.$listeners.click
    },
    hasFloatSlot () {
      return !!this.$slots.float
    },
  },
  watch: {
    fluid: 'setHeight',
    minHeight: 'setHeight',
    minWidth: 'setWidths',
    maxWidth: 'setWidths',
    width: 'setWidths',
    visible: function (newValue) {
      this.isVisible = !!newValue
    },
    isInViewport: function (newValue) {
      if (this.appear) {
        // item came into viewport and appear-option is set
        this.isVisible = !!newValue
      }
    },
  },
  created () {
    this.initiateAppearance()
    this.setHeight()
    this.setWidths()
  },
  methods: {
    initiateAppearance () {
      this.intersectionStop = this.appear ? 0.25 : 0
      this.isVisible = !this.appear && !!this.visible
      this.isInViewport = !this.appear
      this.isLoading = this.video ? false : this.image ? this.isLoading : false // turn of when we have a video
    },
    getFileExtension (filename) {
      return filename.substr(filename.lastIndexOf('.') + 1)
    },
    convertWidthToCss (size) {
      /**
       * Convert size to pixels when a integer has been given
       * @property {String} size - number from 1 to 12 or a fixed amount of 'px'
       */
      if (!size) return null
      if (Number(size) && 0 < size && size <= 12) {
        return (100 / 12 * size) + '%'
      }
      if (typeof size === 'string' && (size.includes('px') || size.includes('calc'))) {
        return size
      }
      return size + 'px'
    },
    async setHeight () {
      if (!this.fluid && this.minHeight) {
        if (this.minHeight.includes('%')) {
          this.isFixedRatio = true
          this.styles.minHeight = null
          this.stylesRatio.paddingBottom = this.minHeight
        }
        if (this.minHeight.includes('px')) {
          this.isFixedRatio = false
          this.styles.minHeight = this.minHeight
          this.stylesRatio.paddingBottom = null
        }
      } else {
        this.isFixedRatio = false
        this.styles.minHeight = null
        this.stylesRatio.paddingBottom = null
      }
    },
    async setWidths () {    
      if (!this.fluid) {
        this.styles.maxWidth = this.convertWidthToCss(this.width) || this.convertWidthToCss(this.maxWidth)
        this.styles.minWidth = this.convertWidthToCss(this.width) || this.convertWidthToCss(this.minWidth)
        this.styles.width = this.convertWidthToCss(this.width)
      }
    },
    async onIntersection (amountVisible) {
      /**
       * Emitted when card enters or leaves the viewport
       * @property {Number} amountVisible - percentage of the card that is visible
       */
      this.isInViewport = amountVisible >= 0.25
    },
    onClick ($event) {
      /**
       * Emitted when the user clicks the card
       */
      this.$emit('click', $event)
    },
    onTap ($event) {
      /**
       * Manual tap event for stable iOS interaction with the card
       * Because :active state doesnt show when card has no touch-listeners
       */
      if (this.hasTouchMoved) return
      this.onClick($event)

      // Fire a click event on the hyperlink
      if (!this.to) return
      $event.preventDefault()
      $event.stopPropagation()
      const anchorTag = this.$refs?.hyperlink?.$el || this.$refs?.hyperlink
      anchorTag && anchorTag.click()
    },
  },
}
